<template>
  <div class="d-flex flex-column-fluid financing-rules-page">
    <div class="container">
      <!-- Page Header -->
      <div class="page-header mb-6">
        <div class="header-content">
          <div class="header-title">
            <h1 class="title">Financing Rules</h1>
            <p class="subtitle">Manage and configure financing rules for business evaluation</p>
          </div>
          <div class="header-actions">
            <button
              class="btn breaches-btn"
              @click="goToBreaches"
              :disabled="loading"
            >
              <i class="las la-exclamation-triangle"></i>
              Breaches
            </button>
            <button 
              class="btn btn-primary add-rule-btn"
              @click="openAddRuleModal"
              :disabled="loading"
            >
              <i class="las la-plus"></i>
              Add Rule
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <div class="spinner-border" role="status"></div>
        <div class="loading-text">Loading rules...</div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <div class="alert alert-danger">
          <i class="material-icons">error</i>
          {{ error }}
        </div>
      </div>

      <!-- Main Content -->
      <div v-else>
        <!-- Empty State -->
        <div
          class="empty-state text-center"
          v-if="isEmpty"
        >
          <i class="las la-cogs la-5x text-muted mb-3"></i>
          <div class="empty-title">No rules found</div>
          <div class="empty-subtitle">
            Create your first financing rule to get started
          </div>
        </div>

        <!-- Rules Table -->
        <div v-else class="rules-table-container">
          <financing-table
            :columns="tableColumns"
            :data="rules"
            :grid-template="gridTemplate"
            :total-rows="totalRules"
            :current-page="currentPage"
            :page-size="pageSize"
            :loading="loadingRules"
            currency="USD"
            @page-change="onPageChanged"
            @action="handleTableAction"
            :on-row-click="onRuleClick"
            :row-class="getRowClass"
            :tooltip="getRowTooltip"
          />
        </div>
      </div>
    </div>

    <!-- Add/Edit Rule Modal -->
    <rule-modal 
      ref="ruleModal" 
      @saved="onRuleSaved"
      @deleted="onRuleDeleted"
    />
  </div>
</template>

<script>
import RulesService from "@/services/rules.service";
import FinancingTable from "@/components/UI/FinancingTable.vue";
import RuleModal from "./components/RuleModal.vue";
import { formatErrorMessage } from "@/utils/random_utils";

export default {
  name: "FinancingRules",
  components: {
    FinancingTable,
    RuleModal
  },
  data() {
    return {
      loading: true,
      loadingRules: false,
      deleting: false,
      error: null,
      
      // Rules data
      rules: [],
      totalRules: 0,
      currentPage: 1,
      pageSize: 20,
      
      // Table configuration
      tableColumns: [
        {
          header: 'Name',
          field: 'name',
          class: 'name-col',
          type: 'text',
        },
        {
          header: 'Description',
          field: 'description',
          class: 'description-col',
          type: 'text',
        },
        {
          header: 'Threshold',
          field: 'threshold',
          class: 'threshold-col',
          type: 'text',
          align: 'text-center'
        },
        {
          header: 'Status',
          field: 'is_active',
          class: 'status-col',
          type: 'status',
          align: 'text-center'
        },
        {
          header: 'Actions',
          field: 'actions',
          class: 'actions-col',
          type: 'action',
          align: 'text-center',
          action: 'delete',
          icon: 'las la-trash',
          tooltip: 'Delete rule'
        }
      ]
    };
  },
  
  computed: {
    isEmpty() {
      return this.rules.length === 0 && !this.loading;
    },
    
    gridTemplate() {
      return '20% 20% 20% 20% 20%';
    }
  },
  
  async mounted() {
    await this.loadRules();
  },
  
  methods: {
    async loadRules(page = 1) {
      try {
        this.loadingRules = page === 1 ? false : true;
        this.loading = page === 1;
        this.error = null;
        
        const params = {
          page,
          page_size: this.pageSize
        };
        
        const response = await RulesService.getRules(params);
        
        this.rules = response.results || response.data || [];
        this.totalRules = response.count || 0;
        this.currentPage = page;
        
      } catch (error) {
        console.error('Error loading rules:', error);
        this.error = formatErrorMessage(error) || 'Failed to load rules. Please try again.';
        this.rules = [];
        this.totalRules = 0;
      } finally {
        this.loading = false;
        this.loadingRules = false;
      }
    },
    
    onPageChanged(page, pageSize) {
      if (pageSize && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
      }
      this.loadRules(page);
    },
    
    openAddRuleModal() {
      this.$refs.ruleModal.openModal();
    },

    goToBreaches() {
      this.$router.push({ name: "financing-breaches" });
    },
    
    onRuleClick(rule) {
      this.$refs.ruleModal.openModal(rule);
    },
    
    handleTableAction(actionData) {
      if (actionData.action === 'delete') {
        this.confirmDeleteRule(actionData.row);
      }
    },
    
    confirmDeleteRule(rule) {
      const message = this.$createElement("div", { class: "text-center" }, [
        this.$createElement("i", { 
          class: "las la-exclamation-triangle text-warning", 
          style: "font-size: 3rem; margin-bottom: 1rem;"
        }),
        this.$createElement("p", { class: "mb-2" }, "Are you sure you want to delete this rule?"),
        this.$createElement("p", { class: "font-weight-bold mb-2" }, `"${rule.name}"`),
      ]);

      this.$bvModal
        .msgBoxConfirm(message, {
          title: "Delete Rule",
          buttonSize: "md",
          okTitle: "Delete Rule",
          okVariant: "danger",
          cancelTitle: "Cancel",
          cancelVariant: "secondary",
          centered: true,
          modalClass: "delete-confirmation-modal",
          headerClass: "border-0 pb-0",
          bodyClass: "pt-2",
          footerClass: "border-0 pt-0"
        })
        .then((confirmed) => {
          if (confirmed) {
            this.deleteRule(rule);
          }
        });
    },
    
    async deleteRule(rule) {
      if (this.deleting) return;
      
      try {
        this.deleting = true;
        await RulesService.deleteRule(rule.id);
        
        // Reload the current page to show updated data
        const maxPage = Math.ceil((this.totalRules - 1) / this.pageSize);
        const targetPage = this.currentPage > maxPage ? Math.max(1, maxPage) : this.currentPage;
        await this.loadRules(targetPage);
        
        // Show success message using Bootstrap Vue toast
        this.$bvToast.toast(`Rule "${rule.name}" has been deleted successfully.`, {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000,
          toaster: 'b-toaster-top-center'
        });
        
      } catch (error) {
        console.error('Error deleting rule:', error);
        const errorMessage = formatErrorMessage(error) || 'Failed to delete rule. Please try again.';
        
        // Show error message using Bootstrap Vue toast
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
          toaster: 'b-toaster-top-center'
        });
      } finally {
        this.deleting = false;
      }
    },
    
    async onRuleSaved() {
      // Reload the current page to show updated data
      await this.loadRules(this.currentPage);
    },
    
    async onRuleDeleted() {
      // If we're on a page that no longer has data, go to previous page
      const maxPage = Math.ceil(this.totalRules / this.pageSize);
      const targetPage = this.currentPage > maxPage ? Math.max(1, maxPage) : this.currentPage;
      await this.loadRules(targetPage);
    },
    
    getRowClass(rule) {
      return {
        'inactive-rule': !rule.is_active
      };
    },
    
    getRowTooltip(rule) {
      if (!rule.is_active) {
        return 'This rule is currently inactive';
      }
      return null;
    }
  }
};
</script>

<style scoped lang="scss">
@import './index.scss';

.financing-rules-page {
  .page-header {
    .header-actions {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: 0.75rem;
      align-items: center;
      flex: 0 0 auto;
      max-width: none;

      .add-rule-btn,
      .breaches-btn {
        border: none;
        border-radius: 0.375rem;
        font-weight: 600;
        padding: 0.5rem 1rem;
        white-space: nowrap;
        transition: all 0.3s ease;

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        i {
          margin-right: 0.5rem;
        }
      }

      .add-rule-btn {
        background: linear-gradient(135deg, #28a745 0%, #20c997 100%);

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
        }
      }

      .breaches-btn {
        background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
        color: #ffffff;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(217, 119, 6, 0.35);
        }
      }
    }
  }
  
  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    
    .spinner-border {
      width: 3rem;
      height: 3rem;
      color: #007bff;
      margin-bottom: 1rem;
    }
    
    .loading-text {
      color: #6c757d;
      font-weight: 500;
    }
  }
  
  .error-state {
    .alert {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      
      i {
        font-size: 1.25rem;
      }
    }
  }
  
  .empty-state {
    padding: 3rem;
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    .empty-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: #495057;
      margin-bottom: 0.5rem;
    }
    
    .empty-subtitle {
      color: #6c757d;
      margin-bottom: 1rem;
    }
  }
  
  .rules-table-container {
    background: white;
    border-radius: 0.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    
    // Custom table styles for rules
    :deep(.financing-table) {
      .grid-header .header-row {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        
        .name-col {
          font-weight: 600;
        }
      }
      
      .grid-body {
        .data-row {
          &.inactive-rule {
            opacity: 0.6;
            background-color: #f8f9fa;
          }
          
          .name-col {
            font-weight: 600;
            color: #212529;
          }
          
          .description-col {
            color: #6c757d;
            font-size: 0.9rem;
          }
          
          .threshold-col {
            font-weight: 500;
            color: #495057;
          }
          
          .status-col {
            text-align: right;
            font-weight: 600;
            font-size: 100rem;
            .status-badge {
              &.active {
                background-color: #d4edda;
                color: #155724;
                border: 1px solid #c3e6cb;
              }
              
              &.inactive {
                background-color: #f8d7da;
                color: #721c24;
                border: 1px solid #f5c6cb;
              }
            }
          }
          
          .actions-col {
            .action-buttons {
              display: flex;
              gap: 0.5rem;
              justify-content: center;
            }
          }
        }
      }
    }
  }
}

// Delete confirmation modal styling
:deep(.delete-confirmation-modal) {
  .modal-header {
    .modal-title {
      font-weight: 600;
      color: #dc3545;
    }
  }
  
  .modal-body {
    .las.la-exclamation-triangle {
      display: block;
      margin: 0 auto 1rem;
    }
  }
  
  .modal-footer {
    .btn {
      border-radius: 0.375rem;
      font-weight: 500;
      padding: 0.5rem 1.5rem;
      
      &.btn-danger {
        background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
        border: none;
        
        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
        }
      }
      
      &.btn-secondary {
        background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
        border: none;
        
        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
        }
      }
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .financing-rules-page {
    .page-header {
      .header-content {
        flex-direction: column;
        gap: 1rem;
        
        .header-actions {
          width: 100%;
          justify-content: center;
          flex-wrap: wrap;
        }
      }
    }
  }
}
</style>
